import '../style.scss';

import { useCallback, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { pageurl } from '@/constants/pageurl';
import { RightSectionContext } from '@/context/right-section';
import { useUnitEstimates } from '@/hooks/queries/cancellations/use-cancellation-impact';

import { DESKTOP_ALERTS_COPY, plural, unitTitle } from '../copy';
import { useCancellationAlerting } from '../hooks/use-cancellation-alerting';
import { useCancellationFeed } from '../hooks/use-cancellation-feed';
import { alertUi } from '../services/alert-ui';
import { cancellationAlerts, type ICancellationAlert } from '../services/cancellation-alerts';
import {
	type DesktopAlertState,
	desktopAlertState,
	disableDesktopAlerts,
	enableDesktopAlerts,
	ringFor,
} from '../services/desktop-alerts';
import { type ICancellationRow } from '../types';
import AlertCard from './AlertCard';

/** Cards on screen at once. A flood shows the newest few and a count — the page holds the rest. */
const VISIBLE = 3;
const TITLE_PREFIX = /^\(\d+\)\s/;

/**
 * On the Cancelled units page the stack would only repeat what the page shows — and, fixed
 * top-right, it covers the ledger's row actions. There it stays collapsed to the bell unless
 * the bell opened it.
 */
export const isStackVisible = (args: {
	eligible: boolean;
	ready: boolean;
	open: boolean;
	explicit: boolean;
	onCancelledUnitsPage: boolean;
	count: number;
}): boolean => args.eligible && args.ready && args.open && args.count > 0 && (!args.onCancelledUnitsPage || args.explicit);

export const titleWithCount = (title: string, unread: number): string => {
	const bare = title.replace(TITLE_PREFIX, '');
	return unread > 0 ? `(${unread}) ${bare}` : bare;
};

const DesktopAlertsControl = () => {
	const [state, setState] = useState<DesktopAlertState>(() => desktopAlertState());
	if (state === 'unsupported') return null;
	if (state === 'denied') return <p className='pc-host__note'>{DESKTOP_ALERTS_COPY.denied}</p>;
	if (state === 'on') {
		return (
			<p className='pc-host__note'>
				{DESKTOP_ALERTS_COPY.on} ·{' '}
				<button
					type='button'
					className='pc-linkbtn'
					onClick={() => {
						disableDesktopAlerts();
						setState('off');
					}}
				>
					Turn off
				</button>
			</p>
		);
	}
	return (
		<button
			type='button'
			className='pc-linkbtn pc-host__offer'
			title={DESKTOP_ALERTS_COPY.offerDetail}
			onClick={() => void enableDesktopAlerts().then(setState)}
		>
			{DESKTOP_ALERTS_COPY.offer}
		</button>
	);
};

/**
 * The shell's cancellation alert stack (mounted in `src/layout/index.tsx`).
 *
 * Alerts PERSIST until the admin acts on or dismisses them: the app's toasts are `limit={1}`
 * for 2 s, which under a flood of INEC cancellations would show one unit and drop the rest.
 * Collapsing hides the stack until the next alert; the header bell reopens it.
 */
const AlertHost = () => {
	const navigate = useNavigate();
	const rightPanel = useContext(RightSectionContext);
	const { scope, eligible, capabilities } = useCancellationAlerting();
	const snapshot = useSyncExternalStore(cancellationAlerts.subscribe, cancellationAlerts.getSnapshot);
	const open = useSyncExternalStore(alertUi.subscribe, alertUi.isOpen);
	const explicit = useSyncExternalStore(alertUi.subscribe, alertUi.isExplicit);
	const { pathname } = useLocation();
	const onCancelledUnitsPage = pathname.startsWith(pageurl.CANCELLED_UNITS);
	// A bell-opened stack does not follow the user onto (or off) the page.
	useEffect(() => {
		alertUi.clearExplicit();
	}, [onCancelledUnitsPage]);

	const openDetail = useCallback(
		(row: ICancellationRow) => {
			rightPanel?.callSection({
				action: 'view',
				component: 'cancellation-detail',
				title: unitTitle(row),
				breadcrumb: ['Cancelled units', row.pollingUnitCode],
				half: true,
				data: { row },
			});
		},
		[rightPanel]
	);
	const openDetailRef = useCallbackRef(openDetail);

	const onRing = useCallback(
		(alert: ICancellationAlert) => {
			alertUi.setOpen(true);
			ringFor(alert, () => openDetailRef.current(alert.row));
		},
		[openDetailRef]
	);

	useCancellationFeed({ scope, capabilities, enabled: eligible, onRing });

	const unread = eligible ? snapshot.unread : 0;
	useEffect(() => {
		document.title = titleWithCount(document.title, unread);
	}, [unread]);
	useEffect(() => () => void (document.title = titleWithCount(document.title, 0)), []);

	const shown = snapshot.alerts.filter((a) => a.unread);
	const hasActiveShown = shown.some((a) => a.active);
	const estimates = useUnitEstimates(scope.electionId, scope.partyId, eligible && open && hasActiveShown);

	if (!capabilities || !isStackVisible({ eligible, ready: true, open, explicit, onCancelledUnitsPage, count: shown.length }))
		return null;

	const visible = shown.slice(0, VISIBLE);
	const more = shown.length - visible.length;

	return (
		<aside
			id='pc-alert-host'
			className='pc-host'
			aria-label='Cancelled polling unit alerts'
		>
			<div className='pc-host__head'>
				<h2 className='pc-host__title'>{plural(shown.length, 'new cancellation')}</h2>
				<div className='pc-host__head-actions'>
					<button
						type='button'
						className='pc-linkbtn'
						onClick={cancellationAlerts.markAllSeen}
					>
						Mark all seen
					</button>
					<button
						type='button'
						className='pc-linkbtn'
						onClick={() => alertUi.setOpen(false)}
						aria-label='Hide cancellation alerts'
					>
						Hide
					</button>
				</div>
			</div>
			<div className='pc-host__stack'>
				{visible.map((alert) => (
					<AlertCard
						key={alert.id}
						alert={alert}
						announce={alert.id === snapshot.newestUnreadId}
						scope={scope}
						capabilities={capabilities}
						estimates={estimates}
						onOpen={() => {
							cancellationAlerts.markSeen(alert.id);
							openDetail(alert.row);
						}}
						onDismiss={() => cancellationAlerts.dismiss(alert.id)}
						onDone={() => cancellationAlerts.markSeen(alert.id)}
					/>
				))}
			</div>
			<div className='pc-host__foot'>
				<button
					type='button'
					className='pc-linkbtn'
					onClick={() => navigate(pageurl.CANCELLED_UNITS)}
				>
					{more > 0 ? `${plural(more, 'more')} — open Cancelled units` : 'Open Cancelled units'}
				</button>
				<DesktopAlertsControl />
			</div>
		</aside>
	);
};

/** A ref that always holds the latest callback, for handlers created once. */
function useCallbackRef<T>(value: T) {
	const [ref] = useState(() => ({ current: value }));
	ref.current = value;
	return ref;
}

export default AlertHost;
